/**
 * Se ESTE usuário pode levantar uma janela de N meses — e o que acontece com
 * os meses dela quando ele mandar gravar.
 *
 * Função pura, sem React e sem fetch, porque a régua precisa ser testável: ela
 * repete a conta que o backend faz em `controllers/periodoController.preview`
 * (`cabe = limite === null || (jaTem + novas) <= limite`) em vez de inventar uma
 * segunda régua — regra duplicada é regra que diverge, e aqui a divergência
 * apareceria como "escolhi 12 meses e o produto guardou 3".
 *
 * TRÊS DECISÕES QUE MANDAM AQUI
 *
 * 1. **O que bloqueia a janela é o TAMANHO dela, não a cota restante.**
 *    Uma janela de 6 meses nunca cabe num plano de 3, faça o usuário o que
 *    fizer. Já a janela de 3 pode caber inteira em quem tem 3 meses lançados
 *    (se forem os mesmos meses), e por isso ela NUNCA é desabilitada: um
 *    gratuito que já usou a cota e visse todas as janelas mortas ficaria num
 *    beco sem saída, pior que o comportamento anterior.
 *
 * 2. **Mês que já existe não consome cota** (`backend/services/periodoStore.js`:
 *    o limite só é aplicado quando não há competência vigente). Então a conta é
 *    sobre os meses NOVOS da janela, não sobre o tamanho dela.
 *
 * 3. **Mês que já existe é SOBREPOSIÇÃO, e sobreposição é correção.** Ela é
 *    permitida sempre — inclusive com a cota estourada — e por isso precisa ser
 *    dita por NOME antes de gravar. É para isso que existe `existentesRotulos`:
 *    quem lê monta a frase "Junho de 2026 já está lançado e vai ser
 *    substituído", e o usuário decide com a informação na mão.
 *
 * Este arquivo deixou de mapear uma LISTA de janelas possíveis. A quantidade de
 * meses agora é digitada livre (`config/planoContas.limitarQuantidadeDeMeses`),
 * então a régua é `janelaPermitida(n)`; `opcoesDeMeses` sobrou apenas para
 * desenhar os atalhos 3 / 6 / 12 com o cadeado certo em cada um.
 */

import { ATALHOS_DE_MESES, MESES_MAXIMO, rotulosMeses } from '../config/planoContas';

/** 'AAAA-MM' — o mesmo formato de competência que o backend grava. */
export const competenciaDe = ({ mes, ano }) => `${ano}-${String(mes).padStart(2, '0')}`;

/** "Abril, maio e junho" — mês bloqueado nomeado lê como acionável; "3 meses" lê como muro. */
export const listarEmFrase = (nomes) => {
  const itens = (nomes || []).filter(Boolean);
  if (!itens.length) return '';
  if (itens.length === 1) return itens[0];
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
};

/**
 * A maior janela que este plano permite escolher.
 *
 * `null` (plano sem limite) para no teto de tela, não no infinito: quem assina
 * não ganha o direito de encarar 40 páginas de formulário.
 */
export const tetoDeMeses = (limiteCompetencias) =>
  limiteCompetencias === null || limiteCompetencias === undefined
    ? MESES_MAXIMO
    : Math.min(Math.max(1, limiteCompetencias), MESES_MAXIMO);

/**
 * O que vai acontecer com CADA mês de uma lista, na hora de gravar.
 *
 * Recebe rótulos de mês (`config/planoContas.rotulosMeses`) e devolve os mesmos
 * objetos separados por desfecho. É a régua compartilhada entre a escolha da
 * janela (onde a lista são todos os meses da janela) e a revisão final (onde a
 * lista são só os meses que o usuário realmente preencheu) — a mesma conta nos
 * dois lugares, para a revisão não desmentir o que a primeira tela prometeu.
 *
 * `bloqueados` são os mais ANTIGOS de propósito: `dashboardDatabase.salvarPeriodos`
 * grava do mês mais recente para trás quando há limite de plano, justamente
 * para o gratuito ficar com os meses que o produto usa em tudo. Listar os mais
 * novos aqui faria o aviso apontar meses que na verdade entram.
 *
 * @returns {{novos, existentes, bloqueados, gravados}} — todos listas de rótulos.
 */
export function destinoDosMeses({
  meses = [],
  limiteCompetencias = null,
  competenciasJaLancadas = []
} = {}) {
  const lancadas = new Set(competenciasJaLancadas || []);
  const restantes =
    limiteCompetencias === null ? null : Math.max(0, limiteCompetencias - lancadas.size);

  const novos = meses.filter((m) => !lancadas.has(competenciaDe(m)));
  const existentes = meses.filter((m) => lancadas.has(competenciaDe(m)));

  const bloqueados =
    restantes !== null && novos.length > restantes ? novos.slice(0, novos.length - restantes) : [];
  const gravados = novos.slice(bloqueados.length);

  return { novos, existentes, bloqueados, gravados };
}

/**
 * @param {Object} entrada
 * @param {number} entrada.n — quantos meses a janela cobre.
 * @param {number|null} entrada.limiteCompetencias — `null` = plano sem limite.
 * @param {string} entrada.nomeDoPlano — vai na mensagem, para o usuário saber de que plano se fala.
 * @param {string[]} entrada.competenciasJaLancadas — 'AAAA-MM' já no histórico da empresa.
 * @param {number} entrada.mesReferencia — o último mês da janela (1-12).
 * @param {number} entrada.anoReferencia
 * @returns {{n, liberado, competencias, novas, novasRotulos, existentes,
 *   existentesRotulos, motivo, aviso}}
 */
export function janelaPermitida({
  n,
  limiteCompetencias = null,
  nomeDoPlano = 'Gratuito',
  competenciasJaLancadas = [],
  mesReferencia,
  anoReferencia
} = {}) {
  const lancadas = new Set(competenciasJaLancadas || []);
  const rotulos = rotulosMeses(mesReferencia, anoReferencia, n);
  const competencias = rotulos.map(competenciaDe);
  const { novos, existentes, bloqueados } = destinoDosMeses({
    meses: rotulos,
    limiteCompetencias,
    competenciasJaLancadas
  });

  const liberado = limiteCompetencias === null ? n <= MESES_MAXIMO : n <= limiteCompetencias;

  const motivo = liberado
    ? null
    : limiteCompetencias === null
      ? `O questionário levanta no máximo ${MESES_MAXIMO} meses de uma vez.`
      : `O plano ${nomeDoPlano} acompanha ${limiteCompetencias} ` +
        `${limiteCompetencias === 1 ? 'mês' : 'meses'} de histórico. ` +
        `Para levantar ${n} ${n === 1 ? 'mês' : 'meses'} de uma vez, é preciso assinar.`;

  // A janela cabe no plano, mas a empresa já gastou a cota em OUTROS meses.
  // Isso não bloqueia o questionário — o diagnóstico na tela sai igual —, só
  // avisa que parte não vai para o histórico salvo. Número que some depois de
  // "salvo com sucesso" é o pior modo de falha deste produto.
  const excedentes = liberado ? bloqueados : [];

  // "já tem 6 de 3 meses guardados" era o texto antigo, e ele aparece de
  // verdade: basta uma empresa com seis meses passar para um plano de três.
  // A frase agora diz os dois números sem fingir que um cabe no outro.
  const aviso = excedentes.length
    ? `Seu plano ${nomeDoPlano} acompanha ${limiteCompetencias} ` +
      `${limiteCompetencias === 1 ? 'mês' : 'meses'} de histórico, e você já tem ` +
      `${lancadas.size} ${lancadas.size === 1 ? 'guardado' : 'guardados'}. ` +
      `${listarEmFrase(excedentes.map((r) => r.longo))} ` +
      `${excedentes.length === 1 ? 'não vai' : 'não vão'} entrar no histórico salvo — ` +
      'o diagnóstico na tela usa todos os meses que você informar.'
    : null;

  return {
    n,
    liberado,
    competencias,
    novas: novos.map(competenciaDe),
    novasRotulos: novos.map((r) => r.longo),
    // Os meses da janela que a empresa JÁ tem. Preenchê-los aqui é corrigir:
    // não consome cota, e substitui o que está guardado.
    existentes: existentes.map(competenciaDe),
    existentesRotulos: existentes.map((r) => r.longo),
    motivo,
    aviso
  };
}

/** Os atalhos (3 / 6 / 12) já com o cadeado de cada um resolvido. */
export function opcoesDeMeses({ opcoes = ATALHOS_DE_MESES, ...resto } = {}) {
  return opcoes.map((n) => janelaPermitida({ ...resto, n }));
}

/**
 * A janela que a tela deve assumir quando a escolhida deixa de ser permitida.
 *
 * Acontece de verdade: a pessoa escolhe 12 meses anonimamente, entra numa conta
 * gratuita e volta ao questionário. Cair para o teto do plano é melhor que
 * travar num passo que o botão não deixa sair.
 *
 * NÃO serve para corrigir o que o usuário está digitando AGORA. Quem digita 7
 * num plano de 3 tem que continuar vendo 7 na tela, com o motivo do bloqueio e
 * o caminho para assinar; trocar o número embaixo do dedo dele é o mesmo que
 * dizer que o produto não entendeu o pedido.
 */
export const maiorJanelaLiberada = (limiteCompetencias) => tetoDeMeses(limiteCompetencias);
